import { CanonicalPresentationModelV1Schema, } from "../contracts/canonical-presentation.js";
import { stableStringify } from "../hashing/canonical-json.js";
import { z } from "zod";
/** Exact Step 4 Slice D named outcomes (Safe Edit lifecycle). */
export const SAFE_EDIT_PRESENTATION_OUTCOMES = [
    "permission_denied",
    "source_unknown",
    "safe_update_available",
    "update_rejected",
    "update_submitted",
    "update_in_progress",
    "update_checks_running",
    "update_blocked_by_checks",
    "update_blocked_by_conflict",
    "update_not_activated",
    "update_failed",
    "update_replaced",
    "update_published",
    "update_history_available",
];
export const SafeEditPresentationOutcomeSchema = z.enum(SAFE_EDIT_PRESENTATION_OUTCOMES);
const PROHIBITED_CLIENT_TERMS = [
    "runId",
    "artifactSetHash",
    "exportBundleHash",
    "businessGraphRevisionId",
    "Quality Gate",
    "worker",
    "tenant",
    "schema",
    "GenerationRun",
    "REJECTED",
    "ACCEPTED_PENDING",
    "QUEUED",
    "GENERATING",
    "QA_PENDING",
    "QA_BLOCKED",
    "CONTRADICTION_BLOCKED",
    "ACTIVATION_REJECTED",
    "SUPERSEDED",
    "RECEIVED",
    "VALIDATION_FAILED",
    "QA_RUNNING",
    "QA_PASS",
    "QA_WARN",
    "QA_FAIL",
    "attemptId",
    "patchId",
];
function projectIdOk(input) {
    return Boolean(input.projectId.trim());
}
function mapLifecycleOrPhase(input) {
    const lc = input.clientLifecycle?.lifecycle ?? null;
    const ph = input.agencyPhase?.phase ?? null;
    if (lc === "REJECTED" || ph === "REJECTED" || ph === "VALIDATION_FAILED")
        return "update_rejected";
    if (lc === "ACCEPTED_PENDING" || ph === "RECEIVED" || ph === "ACCEPTED_PENDING_GENERATION") {
        return "update_submitted";
    }
    if (lc === "QUEUED")
        return "update_submitted";
    if (lc === "GENERATING" || ph === "GENERATING")
        return "update_in_progress";
    if (lc === "QA_PENDING" || ph === "QA_RUNNING")
        return "update_checks_running";
    if (lc === "QA_BLOCKED" || ph === "QA_FAIL")
        return "update_blocked_by_checks";
    if (lc === "CONTRADICTION_BLOCKED" || ph === "CONTRADICTION_BLOCKED")
        return "update_blocked_by_conflict";
    if (lc === "ACTIVATION_REJECTED" || ph === "ACTIVATION_REJECTED")
        return "update_not_activated";
    if (lc === "FAILED" || ph === "FAILED")
        return "update_failed";
    if (lc === "SUPERSEDED" || ph === "SUPERSEDED")
        return "update_replaced";
    if (lc === "ACTIVE" || ph === "ACTIVE")
        return "update_published";
    if (ph === "QA_PASS" || ph === "QA_WARN")
        return "update_in_progress";
    return null;
}
function classifyOutcome(input) {
    if (input.accessDenial === "permission_denied")
        return "permission_denied";
    const mapped = mapLifecycleOrPhase(input);
    if (mapped)
        return mapped;
    if (input.clientLifecycle || input.agencyPhase)
        return "source_unknown";
    if (input.safeUpdateAvailable === true)
        return "safe_update_available";
    if (input.hasHistory === true)
        return "update_history_available";
    return "source_unknown";
}
function baseModel(partial) {
    return CanonicalPresentationModelV1Schema.parse({
        schemaId: "enrein.canonicalPresentation.v1",
        ...partial,
    });
}
const BUCKET_COPY = {
    update_rejected: {
        agencyTitle: "A safe update was rejected",
        agencyReason: "The client's safe-update request failed validation before processing began.",
        clientTitle: "This update could not be accepted",
        condition: "NEEDS_ATTENTION",
        agencyRecovery: "Review this safe update with the client if needed.",
        clientRecovery: "You can safely try submitting the update again with corrected information.",
        agencyExpectedResult: "A corrected safe update can be resubmitted at any time.",
        clientExpectedResult: "You can safely try again — your saved information is unaffected.",
    },
    update_submitted: {
        agencyTitle: "A safe update was submitted",
        agencyReason: "A client safe-update request is waiting to process.",
        clientTitle: "Your update was submitted",
        condition: "IN_PROGRESS",
        agencyRecovery: null,
        clientRecovery: null,
        agencyExpectedResult: "The update processes automatically and appears here when live.",
        clientExpectedResult: "Your update processes automatically and appears here when live.",
    },
    update_in_progress: {
        agencyTitle: "A safe update is processing",
        agencyReason: "The client's safe-update request is actively processing.",
        clientTitle: "Your update is processing",
        condition: "IN_PROGRESS",
        agencyRecovery: null,
        clientRecovery: null,
        agencyExpectedResult: "The update becomes visible here automatically once processing finishes.",
        clientExpectedResult: "Your update becomes visible here automatically once processing finishes.",
    },
    update_checks_running: {
        agencyTitle: "Quality checks are running on a safe update",
        agencyReason: "Quality checks are running on the client's safe-update request.",
        clientTitle: "Quality checks are running on your update",
        condition: "IN_PROGRESS",
        agencyRecovery: null,
        clientRecovery: null,
        agencyExpectedResult: "The update publishes automatically once checks pass.",
        clientExpectedResult: "Your update publishes automatically once checks pass.",
    },
    update_blocked_by_checks: {
        agencyTitle: "A safe update is blocked by quality checks",
        agencyReason: "Quality checks blocked the client's safe-update request.",
        clientTitle: "Quality checks blocked this update",
        condition: "NEEDS_ATTENTION",
        agencyRecovery: "Review this safe update with the client if needed.",
        clientRecovery: "Contact your agency for help with this update.",
        agencyExpectedResult: "Once checks pass, a corrected update can proceed to publish.",
        clientExpectedResult: "Once resolved, your update can proceed to publish.",
    },
    update_blocked_by_conflict: {
        agencyTitle: "A safe update is blocked by conflicting information",
        agencyReason: "Conflicting business details blocked the client's safe-update request.",
        clientTitle: "Conflicting details blocked this update",
        condition: "NEEDS_ATTENTION",
        agencyRecovery: "Review this safe update with the client if needed.",
        clientRecovery: "Contact your agency for help with this update.",
        agencyExpectedResult: "Once the conflicting details are resolved, the update can proceed.",
        clientExpectedResult: "Once resolved, your update can proceed.",
    },
    update_not_activated: {
        agencyTitle: "A safe update could not be activated",
        agencyReason: "The client's safe-update request could not become the active website version.",
        clientTitle: "This update could not be activated",
        condition: "NEEDS_ATTENTION",
        agencyRecovery: "Review this safe update with the client if needed.",
        clientRecovery: "Contact your agency for help with this update.",
        agencyExpectedResult: "A corrected update can be resubmitted for activation.",
        clientExpectedResult: "You can safely try again — your saved information is unaffected.",
    },
    update_failed: {
        agencyTitle: "A safe update failed to process",
        agencyReason: "The client's safe-update request failed to process.",
        clientTitle: "This update could not be processed",
        condition: "NEEDS_ATTENTION",
        agencyRecovery: "Review this safe update with the client if needed.",
        clientRecovery: "Contact your agency for help with this update.",
        agencyExpectedResult: "Retrying the update may complete it successfully.",
        clientExpectedResult: "You can safely try again — your saved information is unaffected.",
    },
    update_replaced: {
        agencyTitle: "A safe update was replaced by a newer one",
        agencyReason: "A newer safe-update request replaced this one before it finished.",
        clientTitle: "A newer update replaced this one",
        condition: "NEEDS_ATTENTION",
        agencyRecovery: "No action is needed — the newer update is being processed instead.",
        clientRecovery: "No action is needed. If you still want this change, submit it again.",
        agencyExpectedResult: "The newer safe update continues processing on its own.",
        clientExpectedResult: "The newer update continues processing on its own.",
    },
    update_published: {
        agencyTitle: "A safe update is live",
        agencyReason: "The client's safe-update request is now active on the website.",
        clientTitle: "Your update is now live",
        condition: "COMPLETE",
        agencyRecovery: null,
        clientRecovery: null,
        agencyExpectedResult: "The client's website reflects this update.",
        clientExpectedResult: "Your update is now visible on your website.",
    },
};
function pairForBucket(outcome, input) {
    const projectId = input.projectId.trim();
    const copy = BUCKET_COPY[outcome];
    const clientSafeReason = input.clientLifecycle?.clientSafeReason?.trim();
    const needsAttention = copy.condition === "NEEDS_ATTENTION";
    return {
        outcome,
        agency: baseModel({
            role: "AGENCY",
            journeyStage: "Safe update",
            condition: copy.condition,
            title: copy.agencyTitle,
            reason: copy.agencyReason,
            primaryAction: needsAttention
                ? { intent: "OPEN_SAFE_EDIT_ATTENTION", projectId, label: "Review safe update" }
                : null,
            secondaryAction: null,
            waitingOn: null,
            blockers: needsAttention ? [copy.agencyReason] : [],
            progress: null,
            expectedResult: copy.agencyExpectedResult,
            recovery: copy.agencyRecovery,
            help: null,
            relevantRecords: [`${projectId} — ${outcome.replace(/_/g, " ")}`],
            advancedDetails: null,
        }),
        client: baseModel({
            role: "CLIENT",
            journeyStage: "Safe update",
            condition: copy.condition,
            title: copy.clientTitle,
            reason: clientSafeReason || copy.clientTitle,
            primaryAction: null,
            secondaryAction: null,
            waitingOn: copy.condition === "IN_PROGRESS" ? "Enrein" : needsAttention ? "Agency" : null,
            blockers: needsAttention ? [copy.clientTitle] : [],
            progress: null,
            expectedResult: copy.clientExpectedResult,
            recovery: copy.clientRecovery,
            help: null,
            relevantRecords: [],
            advancedDetails: null,
        }),
    };
}
function permissionDeniedPair() {
    return {
        outcome: "permission_denied",
        agency: baseModel({
            role: "AGENCY",
            journeyStage: "Safe update",
            condition: "NEEDS_ATTENTION",
            title: "Access to this safe update was denied",
            reason: "A safe-update access attempt failed a permission or tenant check.",
            primaryAction: null,
            secondaryAction: null,
            waitingOn: null,
            blockers: ["Access denied"],
            progress: null,
            expectedResult: null,
            recovery: "Confirm the correct project and permissions before retrying.",
            help: null,
            relevantRecords: [],
            advancedDetails: null,
        }),
        client: baseModel({
            role: "CLIENT",
            journeyStage: "Safe update",
            condition: "NEEDS_ATTENTION",
            title: "Access is not available",
            reason: "You do not have permission to view this safe update.",
            primaryAction: null,
            secondaryAction: null,
            waitingOn: null,
            blockers: [],
            progress: null,
            expectedResult: null,
            recovery: "Ask your agency for a new secure link if you still need access.",
            help: null,
            relevantRecords: [],
            advancedDetails: null,
        }),
    };
}
function sourceUnknownPair(projectId) {
    return {
        outcome: "source_unknown",
        agency: baseModel({
            role: "AGENCY",
            journeyStage: "Safe update",
            condition: "NEEDS_ATTENTION",
            title: "Safe update status could not be confirmed",
            reason: "The safe-update source returned an unrecognized or incomplete state.",
            primaryAction: null,
            secondaryAction: null,
            waitingOn: null,
            blockers: ["Safe update status is unknown"],
            progress: null,
            expectedResult: null,
            recovery: "Nothing was marked complete. Refresh the project and try again.",
            help: null,
            relevantRecords: [`${projectId} — safe update status unknown`],
            advancedDetails: null,
        }),
        client: baseModel({
            role: "CLIENT",
            journeyStage: "Safe update",
            condition: "NEEDS_ATTENTION",
            title: "We could not confirm the current status",
            reason: "The action was not completed and nothing was marked complete.",
            primaryAction: null,
            secondaryAction: null,
            waitingOn: null,
            blockers: [],
            progress: null,
            expectedResult: null,
            recovery: "Your saved information remains available. Please check back shortly.",
            help: null,
            relevantRecords: [],
            advancedDetails: null,
        }),
    };
}
function safeUpdateAvailablePair() {
    return {
        outcome: "safe_update_available",
        agency: baseModel({
            role: "AGENCY",
            journeyStage: "Safe update",
            condition: "WAITING",
            title: "Client can submit a safe update",
            reason: "The client may submit a self-service safe update to their business information at any time.",
            primaryAction: null,
            secondaryAction: null,
            waitingOn: "Client",
            blockers: [],
            progress: null,
            expectedResult: "A submitted safe update processes automatically.",
            recovery: null,
            help: null,
            relevantRecords: [],
            advancedDetails: null,
        }),
        client: baseModel({
            role: "CLIENT",
            journeyStage: "Safe update",
            condition: "READY",
            title: "Make a safe update",
            reason: "You can safely update parts of your business information yourself.",
            primaryAction: { intent: "OPEN_SAFE_UPDATE", label: "Make a safe update" },
            secondaryAction: null,
            waitingOn: null,
            blockers: [],
            progress: null,
            expectedResult: "Your update processes automatically and appears here when live.",
            recovery: null,
            help: null,
            relevantRecords: [],
            advancedDetails: null,
        }),
    };
}
function updateHistoryAvailablePair() {
    return {
        outcome: "update_history_available",
        agency: baseModel({
            role: "AGENCY",
            journeyStage: "Safe update",
            condition: "READY",
            title: "Safe update history is available",
            reason: "Past client safe-update attempts are available to review.",
            primaryAction: null,
            secondaryAction: { label: "View safe update history", kind: "route" },
            waitingOn: null,
            blockers: [],
            progress: null,
            expectedResult: null,
            recovery: null,
            help: null,
            relevantRecords: [],
            advancedDetails: null,
        }),
        client: baseModel({
            role: "CLIENT",
            journeyStage: "Safe update",
            condition: "READY",
            title: "Your update history",
            reason: "You can review your past safe updates.",
            primaryAction: null,
            secondaryAction: { label: "View update history", kind: "route" },
            waitingOn: null,
            blockers: [],
            progress: null,
            expectedResult: null,
            recovery: null,
            help: null,
            relevantRecords: [],
            advancedDetails: null,
        }),
    };
}
/**
 * Fail-closed paired Agency/Client Safe Edit presentation (ENR-UX-009 Step 4 Slice D).
 * Shapes presentation only — never re-implements SafeEdit lifecycle math. Accepts a
 * pre-resolved Client SafeEditLifecycleState and/or Agency AgencyEditorPatchPhase.
 */
export function derivePairedSafeEditPresentation(input) {
    if (!projectIdOk(input))
        return null;
    const outcome = classifyOutcome(input);
    if (!outcome)
        return null;
    const projectId = input.projectId.trim();
    switch (outcome) {
        case "permission_denied":
            return permissionDeniedPair();
        case "source_unknown":
            return sourceUnknownPair(projectId);
        case "safe_update_available":
            return safeUpdateAvailablePair();
        case "update_history_available":
            return updateHistoryAvailablePair();
        default:
            return pairForBucket(outcome, input);
    }
}
export function canonicalizeSafeEditPresentation(model) {
    return stableStringify(model);
}
/** Fail-closed Client terminology scan for presentation copy. */
export function clientSafeEditContainsProhibitedTerminology(model) {
    if (model.role !== "CLIENT")
        return false;
    const blob = [
        model.title,
        model.reason,
        model.expectedResult ?? "",
        model.recovery ?? "",
        model.help ?? "",
        model.advancedDetails ?? "",
        ...model.blockers,
        ...model.relevantRecords,
        model.primaryAction?.label ?? "",
    ].join("\n");
    return PROHIBITED_CLIENT_TERMS.some((term) => blob.includes(term));
}
//# sourceMappingURL=derive-safe-edit-presentation.js.map